import React from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid, TextField, MenuItem, Alert, Typography, Box } from "@mui/material";
import { StepUpSpinner } from "../../../../Components/Loader";
import { paymentsApi } from "./api";

type Props = {
  open: boolean;
  parentId: string | number;
  card: any;
  onClose: () => void;
  onSuccess: () => void;
};

const EditCardDialog: React.FC<Props> = ({ open, parentId, card, onClose, onSuccess }) => {
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [cardholderName, setName] = React.useState(card.holderName || "");
  const [expiryMonth, setMonth] = React.useState<number>(Number(card.expiryMonth) || 1);
  const [expiryYear, setYear]   = React.useState<number>(Number(card.expiryYear) || new Date().getFullYear());

  const submit = async () => {
    if (busy) return;
    setBusy(true); setError(null);
    try {
      await paymentsApi.updateMethod(parentId, card.id, {
        cardholder_name: cardholderName,
        expiry_month: expiryMonth,
        expiry_year: expiryYear,
      });
      onSuccess();
    } catch (e: any) {
      const msg = e?.message ?? "Update failed.";
      setError(/not configured/i.test(msg) ? "Worldline payment gateway is not configured." : msg);
    } finally { setBusy(false); }
  };

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="xs" fullWidth
      disableEscapeKeyDown={busy}
      PaperProps={{ sx: { position: "relative", overflow: "hidden" } }}
    >
      <DialogTitle sx={{ fontWeight: 700 }}>Edit Card</DialogTitle>
      <DialogContent>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
          Card number cannot be edited. To use a different card, add a new one and delete the old.
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField fullWidth label="Cardholder Name" value={cardholderName} onChange={(e) => setName(e.target.value)} disabled={busy} />
          </Grid>
          <Grid item xs={6}>
            <TextField select fullWidth label="Exp Month" value={expiryMonth} onChange={(e) => setMonth(Number(e.target.value))} disabled={busy}>
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => <MenuItem key={m} value={m}>{String(m).padStart(2, "0")}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField select fullWidth label="Exp Year" value={expiryYear} onChange={(e) => setYear(Number(e.target.value))} disabled={busy}>
              {Array.from({ length: 12 }, (_, i) => new Date().getFullYear() + i).map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
            </TextField>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={busy}>{busy ? "Saving…" : "Save"}</Button>
      </DialogActions>
      {busy && (
        <Box
          aria-live="polite" aria-busy="true"
          sx={{
            position: "absolute", inset: 0, zIndex: 10,
            display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center",
            gap: 1.5,
            bgcolor: "rgba(255,255,255,0.86)",
            backdropFilter: "blur(3px)",
            WebkitBackdropFilter: "blur(3px)",
          }}
        >
          <StepUpSpinner size="large" />
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: "text.primary", mt: 1 }}>
            Saving card details…
          </Typography>
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
            Please wait while we update your card with Worldline.
          </Typography>
        </Box>
      )}
    </Dialog>
  );
};

export default EditCardDialog;
